import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class Store {

  private _cache: { [key: string]: ErrorWrap<State<any>> } = {};

  setValue<T>(key: string, value: T | null | undefined) {
    if (this._cache[key]) {
      this._cache[key].value.setValue(value);
      this._cache[key].error = '';
      return
    }
    this._cache[key] = new ErrorWrap(new State<T>().setValue(value), '');
  }

  setError<T>(key: string, error: string) {
    if (this._cache[key]) {
      this._cache[key].error = error;
      this._cache[key].value.setValue(null);
      return;
    }
    this._cache[key] = new ErrorWrap(new State<T>().setValue(undefined), error);
  }

  hasError(key: string): boolean {
    return this._cache[key] !== undefined && this._cache[key].error !== '';
  }

  /** The error a key was last set to; empty when it has none. */
  getError(key: string): string {
    return this._cache[key]?.error ?? '';
  }

  isExpired(key: string): boolean {
    return this._cache[key] === undefined || (this._cache[key].ttl !== 0 && this._cache[key].ttl < Date.now());
  }

  setExpiryMs(key: string, ttl: number) {
    if (this._cache[key]) {
      this._cache[key].ttl = ttl === 0 ? ttl : ttl + Date.now();
    }
  }

  register<T>(key: string): this {
    if (!this._cache[key]) {
      this._cache[key] = new ErrorWrap(new State<T>().setValue(null));
    }
    return this;
  }

  getValue<T>(key: string): Observable<T | null | undefined> {
    if (!this._cache[key]) {
      this._cache[key] = new ErrorWrap(new State<T>());
    }
    return this._cache[key].value.getValue();
  }

  hasValidValue(key: string): boolean {
    return this._cache[key] !== undefined && this._cache[key].value.getSnapshotValue() !== null && !this.hasError(key);
  }

  /** Unlike `hasValidValue`, false for the `undefined` a registered-but-unfilled key holds. */
  hasSettledValue(key: string): boolean {
    const entry = this._cache[key];
    if (entry === undefined || this.hasError(key)) {
      return false;
    }
    const value = entry.value.getSnapshotValue();
    return value !== null && value !== undefined;
  }

  removeKey(key: string) {
    if (this._cache[key]) {
      this._cache[key].value.setValue(null);
    }
  }
}

class State<T> {
  private readonly trigger$$ = new BehaviorSubject<T | null | undefined>(undefined);
  private readonly observer$: Observable<T  | null | undefined> = this.trigger$$.asObservable();

  setValue(value: T | null | undefined): this {
    this.trigger$$.next(value);
    return this;
  }

  getSnapshotValue(): T | null | undefined {
    return this.trigger$$.getValue()
  }

  getValue(): Observable<T | null | undefined> {
    return this.observer$;
  }
}

class ErrorWrap<T> {
  constructor(public value: T, public error = '', public ttl = 0) {
  }
}
